/**
 * Where recognition sends images, if anywhere.
 *
 * **Proxy mode is the default, and it is not conditional on anything the build
 * can see.** The browser posts a photo to `/api/vision` on its own origin; a
 * handler there attaches the key. No CORS preflight to fail, no key in the
 * bundle.
 *
 * ### Why this used to be conditional, and why that was wrong
 *
 * Proxy mode was gated on `VITE_VISION_PROXY`, which `vite.config.ts` set to
 * "1" only when it could see `NVIDIA_API_KEY` **at build time**. On a laptop
 * with `.env.local` that is true, so it worked in dev for months.
 *
 * On Cloudflare the key is a *runtime secret*. `vite build` never sees it, the
 * flag was never set, `readVisionConfig()` returned null, and recognition was
 * not broken in production — it was **switched off**, silently, with no error
 * anywhere because the client never made a request to fail.
 *
 * The mistake was letting a build-time fact stand in for a runtime one. Whether
 * a key exists is the server's business; the client's business is to ask and to
 * report what came back. It now always asks, and `/api/vision` answers 503 with
 * `not-configured` when there is no key — which the UI can say in words.
 *
 * The one alternative:
 *
 *   VITE_NVIDIA_API_KEY=...                      → **direct mode**
 *       The browser calls NVIDIA itself with the key inlined into the bundle.
 *       Only works if the endpoint sends CORS headers for your origin, and the
 *       key is readable by anyone who opens the site. Kept for pointing at a
 *       hosted proxy of your own that expects a token.
 *
 * `VITE_VISION_PROXY=0` disables recognition outright, for a static host with
 * no function behind it — otherwise every photo would cost a pointless 404.
 */

const NVIDIA_CHAT_COMPLETIONS = "https://integrate.api.nvidia.com/v1/chat/completions";
/** Matches the route served by vite.config.ts in dev and functions/api/vision.ts in production. */
const PROXY_PATH = "/api/vision";
const DEFAULT_MODEL = "meta/llama-3.2-11b-vision-instruct";

export type VisionMode = "proxy" | "direct";

export interface VisionConfig {
  endpoint: string;
  model: string;
  mode: VisionMode;
  /** Sent as a bearer token. Null in proxy mode — the proxy adds its own. */
  apiKey: string | null;
}

/**
 * `import.meta.env` exists under Vite but not in every bundler or test harness,
 * so it's read defensively rather than assumed.
 */
function env(): Partial<ImportMetaEnv> {
  try {
    return (import.meta.env ?? {}) as Partial<ImportMetaEnv>;
  } catch {
    return {};
  }
}

/** The config, or null only when recognition has been explicitly turned off. */
export function readVisionConfig(): VisionConfig | null {
  const values = env();
  const endpointOverride = values.VITE_VISION_ENDPOINT?.trim();
  const model = values.VITE_VISION_MODEL?.trim() || DEFAULT_MODEL;

  // The one way to have no recognition at all: say so. For a static host with
  // no function behind it, where every photo would otherwise buy a 404.
  if (values.VITE_VISION_PROXY === "0") return null;

  // An explicit "1" forces the proxy even if an inline key is also lying
  // around, so a stray VITE_NVIDIA_API_KEY can never quietly start shipping
  // itself to every visitor.
  if (values.VITE_VISION_PROXY === "1") {
    return { mode: "proxy", apiKey: null, model, endpoint: endpointOverride || PROXY_PATH };
  }

  // Direct mode is opt-in and ships a key in the bundle, so it only happens
  // when someone has deliberately set that variable.
  const apiKey = values.VITE_NVIDIA_API_KEY?.trim();
  if (apiKey) {
    return {
      mode: "direct",
      apiKey,
      model,
      endpoint: endpointOverride || NVIDIA_CHAT_COMPLETIONS,
    };
  }

  // Otherwise: ask our own origin, always. Whether a key exists there is a
  // runtime fact, and the answer comes back as a status we can explain.
  return { mode: "proxy", apiKey: null, model, endpoint: endpointOverride || PROXY_PATH };
}

/**
 * Whether the client has anywhere to send a photo.
 *
 * Deliberately *not* "is there a key" — the client cannot know that, and
 * pretending it could is what broke production. A false here means recognition
 * was switched off at build time; everything else is answered by asking.
 */
export function isVisionConfigured(): boolean {
  return readVisionConfig() !== null;
}

/** True in `npm run dev`, false in a build. Used to show setup hints only in dev. */
export function isDevBuild(): boolean {
  return env().DEV === true;
}
